/** @jsx jsx */
import { FC } from 'react';
import { jsx } from '@emotion/core';
import { graphql } from 'gatsby';

import {
  Link as RebassLink,
  Button,
  LinkProps as RebassLinkProps,
  ButtonProps
} from 'rebass';

export const query = graphql`
  fragment Links on ContentfulLinkButton {
    id
    styledAs
    slug
    visibleText
    newTab
    color
    internal {
      type
    }
  }
`;

export interface LinkProps extends RebassLinkProps {
  id?: string;
  disabled?: boolean;
  styledAs?: boolean;
  slug?: string;
  newTab?: boolean;
  visibleText?: string;
  plain?: boolean;
  variant?: 'primaryLink' | 'secondaryLink' | 'plainLink' | 'tab' | '';
  color?: 'White' | 'Fuschia' | 'Teal' | 'Purple' | 'Mustard' | 'None' | '';
}

const Link: FC<JSX.IntrinsicElements['a'] & LinkProps & ButtonProps> = ({
  children,
  styledAs,
  slug,
  newTab,
  plain = false,
  variant,
  color,
  ...rest
}) => {

  const getVariant = () => {
    if (variant === null || variant === '') {
      variant = 'primaryLink'
    } else if (!plain) {
      variant = 'secondaryLink';
    } else if (plain) {
      variant = 'plainLink';
    }
    return variant;
  };

  const linkChecker = (slug) => {
    if (slug) {
      if (slug.includes('bootuppd.org') || slug[0] === '/') {
        if (slug[-1] !== '/') {
          slug = slug + `/`;
        }
      }
    }
    return slug;
  }

  slug = linkChecker(slug);

  let computedBackgroundColor;
  let computedTextColor;

  if (color === 'White') {
    computedBackgroundColor = 'background';
    computedTextColor = 'text';
  } else if (color === 'Fuschia') {
    computedBackgroundColor = 'fuschia';
    computedTextColor = 'background';
  } else if (color === 'Purple') {
    computedBackgroundColor = 'purple';
    computedTextColor = 'background';
  } else if (color === 'Teal') {
    computedBackgroundColor = 'primary';
    computedTextColor = 'text';
  } else if (color === 'Mustard') {
    computedBackgroundColor = 'mustard';
    computedTextColor = 'text';
  } else if (color === 'None') {
    computedBackgroundColor = 'transparent';
    computedTextColor = 'text';
  } else {
    computedBackgroundColor = 'purple';
    computedTextColor = 'background';
  }

  return styledAs || plain ? (
    <RebassLink
      href={slug}
      variant={variant || getVariant()}
      {...(newTab && { target: '_blank', rel: 'noreferrer' })}
      data-gtm-link-track={`website-slug--${slug.replace('/', '')}`}
      {...rest}
    >
      {children}
    </RebassLink>
  ) : (
    <Button
      as="a"
      href={slug}
      variant={variant ? 'primary' : 'secondary'}
      bg={computedBackgroundColor}
      {...(newTab && { target: '_blank', rel: 'noreferrer' })}
      data-variant={variant}
      data-gtm-button-track={
        slug ? `website-slug--${slug.replace('/', '')}` : 'empty-link'
      }
      sx={{
        borderColor: computedBackgroundColor,
        color: computedTextColor
      }}
      {...rest}
    >
      {children}
    </Button>
  );
};

export default Link;
